import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Send, FileText, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailComposeDialog({ 
  open, 
  onOpenChange, 
  recipient, // { email, name, contact_id, booking_id }
  variables = {}, // Pre-filled template variables
  onEmailSent 
}) {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sending, setSending] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  
  const [emailForm, setEmailForm] = useState({
    subject: '',
    body: ''
  });

  useEffect(() => {
    if (open) {
      fetchTemplates();
      // Reset form when dialog opens
      setEmailForm({ subject: '', body: '' });
      setSelectedTemplate(null);
    }
  }, [open]);

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const [templatesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/email-templates`),
        axios.get(`${API}/email-categories`)
      ]);
      setTemplates(templatesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = (templateId) => {
    if (templateId === 'none') {
      setSelectedTemplate(null);
      setEmailForm({ subject: '', body: '' });
      return;
    }
    
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      // Replace variables in subject and body
      let subject = template.subject;
      let body = template.body;
      
      Object.entries(variables).forEach(([key, value]) => {
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        subject = subject.replace(regex, value || '');
        body = body.replace(regex, value || '');
      });
      
      setEmailForm({ subject, body });
    }
  };

  const handleSend = async () => {
    if (!emailForm.subject.trim() || !emailForm.body.trim()) {
      toast.error('Please fill in subject and body');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/communications/send`, {
        recipient_email: recipient.email,
        recipient_name: recipient.name,
        subject: emailForm.subject,
        body: emailForm.body,
        contact_id: recipient.contact_id,
        booking_id: recipient.booking_id,
        template_id: selectedTemplate?.id,
        template_name: selectedTemplate?.name,
        variables: variables
      });
      
      toast.success('Email sent successfully');
      onOpenChange(false);
      if (onEmailSent) onEmailSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  // Group templates by category
  const groupedTemplates = () => {
    const grouped = {};
    templates.forEach(t => {
      const catName = t.category_name || 'Uncategorized';
      if (!grouped[catName]) grouped[catName] = [];
      grouped[catName].push(t);
    });
    return grouped;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-neon-blue" />
            Send Email
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Recipient Info */}
          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-sm">
              <span className="text-muted-foreground">To:</span>{' '}
              <span className="font-medium">{recipient?.name}</span>{' '}
              <span className="text-muted-foreground">&lt;{recipient?.email}&gt;</span>
            </p>
          </div>

          {/* Template Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Use Template (Optional)
            </Label>
            {loadingTemplates ? (
              <div className="text-sm text-muted-foreground">Loading templates...</div>
            ) : (
              <Select 
                value={selectedTemplate?.id || "none"} 
                onValueChange={handleTemplateSelect}
              >
                <SelectTrigger className="bg-muted/30">
                  <SelectValue placeholder="Select a template or write custom email" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Template - Write Custom</SelectItem>
                  {Object.entries(groupedTemplates()).map(([catName, catTemplates]) => (
                    <div key={catName}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase">
                        {catName}
                      </div>
                      {catTemplates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label>Subject *</Label>
            <Input
              value={emailForm.subject}
              onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
              placeholder="Email subject"
              className="bg-muted/30"
            />
          </div>

          {/* Body */}
          <div className="space-y-2">
            <Label>Message *</Label>
            <textarea
              value={emailForm.body}
              onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
              placeholder="Write your message here..."
              className="w-full h-48 px-3 py-2 bg-muted/30 border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-neon-blue/50 resize-none"
            />
          </div>

          {/* Available Variables Info */}
          {Object.keys(variables).length > 0 && (
            <div className="p-3 bg-muted/20 rounded-lg text-xs">
              <p className="font-medium text-muted-foreground mb-1">Available variables in templates:</p>
              <div className="flex flex-wrap gap-1">
                {Object.keys(variables).map(key => (
                  <code key={key} className="px-1.5 py-0.5 bg-muted/50 rounded text-neon-blue">
                    {`{{${key}}}`}
                  </code>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={sending}
            className="bg-neon-blue text-black hover:bg-neon-blue/80"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
